/* eslint-disable indent */
// Libraries
import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { orderBy, isEmpty } from 'lodash';

import {
    getFormattedGranularity,
    checkTabData,
} from '../../utils/helperMethods';

// Styles
import './graphSwitch.scss';

// Components
import { FormElems, Charts, ChartNoData } from '..';

const getLegendsList = values => {
    if (values) {
        const legends = Object.values(values);
        return legends;
    }
    return [];
};

const getLegendsOptions = legends => {
    if (legends) {
        const keys = Object.keys(legends).map(item => {
            return {
                name: legends[item].label,
                value: item,
            };
        });
        if (!keys.some(item => item.value === 'all')) {
            return [{ name: 'All', value: 'all' }].concat(
                orderBy(keys, ['value'], ['asc'])
            );
        }
        return orderBy(keys, ['value'], ['asc']);
    }
    return [];
};

const GraphSwitch = props => {
    const {
        customClass,
        defaultType,
        title,
        canSwitch,
        data: { granularity, timeSeriesValues },
        legends,
        staticPayValues,
    } = props;
    const [chartType, setChartType] = useState(defaultType);
    const [selectedLabel, setSelectedLabel] = useState('all');

    const handleClick = value => {
        setChartType(value);
    };

    const handleLegends = value => {
        setSelectedLabel(value);
    };

    const legendsList = useMemo(() => getLegendsList(legends), [legends]);

    return (
        <div className={`graph-switch-wrap ${customClass}`}>
            <div className="graph-header">
                <span className="graph-title">{title}</span>
                {canSwitch ? (
                    <div className="graph-switch">
                        <button
                            type="button"
                            className={chartType === 'line' ? 'active' : ''}
                            onClick={() => handleClick('line')}
                        >
                            <svg
                                width="38"
                                height="32"
                                viewBox="0 0 38 32"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M4.14751 30.8428C6.43431 30.8428 8.29365 27.7677 8.29365 23.9852C8.29365 22.2152 7.88268 20.6041 7.21441 19.3866L9.65077 15.3565C10.387 16.4624 11.3605 17.1426 12.4309 17.1426C13.5012 17.1426 14.4748 16.4624 15.2117 15.3575L18.3479 20.5438C17.6796 21.7623 17.2686 23.3723 17.2686 25.1424C17.2686 28.9239 19.128 32 21.4148 32C23.7015 32 25.5609 28.9249 25.5609 25.1424C25.5609 23.96 25.3791 22.8461 25.0593 21.874L31.0738 11.9282C31.81 13.0341 32.7835 13.7143 33.8539 13.7143C36.1407 13.7143 38 10.6392 38 6.85664C38 3.07412 36.1407 0 33.8539 0C31.5671 0 29.7077 3.07512 29.7077 6.85664C29.7077 8.03899 29.8895 9.15292 30.2093 10.125L24.1949 20.0708C23.4587 18.9649 22.4851 18.2847 21.4148 18.2847C20.6991 18.2847 20.0268 18.5856 19.4381 19.1149L16.0748 13.5523C16.3952 12.5812 16.577 11.4673 16.577 10.285C16.577 6.50344 14.7177 3.42832 12.4309 3.42832C10.1441 3.42832 8.28474 6.50344 8.28474 10.285C8.28474 11.4673 8.46655 12.5812 8.78628 13.5533L6.12281 17.9577C5.53482 17.4284 4.86175 17.1275 4.14614 17.1275C1.85935 17.1275 0 20.2026 0 23.9852C0 27.7677 1.86003 30.8408 4.14683 30.8408L4.14751 30.8428ZM33.8539 2.28622C35.3784 2.28622 36.6182 4.33697 36.6182 6.85765C36.6182 9.37832 35.3784 11.4291 33.8539 11.4291C32.3293 11.4291 31.0895 9.37832 31.0895 6.85765C31.0895 4.33697 32.3293 2.28622 33.8539 2.28622V2.28622ZM24.1791 25.1434C24.1791 27.664 22.9393 29.7148 21.4148 29.7148C19.8902 29.7148 18.6504 27.664 18.6504 25.1434C18.6504 22.6227 19.8902 20.5719 21.4148 20.5719C22.9393 20.5719 24.1791 22.6227 24.1791 25.1434ZM12.4316 5.71454C13.9561 5.71454 15.1959 7.76529 15.1959 10.286C15.1959 12.8066 13.9561 14.8574 12.4316 14.8574C10.907 14.8574 9.66724 12.8066 9.66724 10.286C9.66724 7.76529 10.907 5.71454 12.4316 5.71454V5.71454ZM4.14751 19.4137C5.67204 19.4137 6.91184 21.4645 6.91184 23.9852C6.91184 26.5058 5.67204 28.5566 4.14751 28.5566C2.62298 28.5566 1.38319 26.5058 1.38319 23.9852C1.38319 21.4645 2.62298 19.4137 4.14751 19.4137V19.4137Z" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            className={chartType === 'bar' ? 'active' : ''}
                            onClick={() => handleClick('bar')}
                        >
                            <svg
                                width="38"
                                height="32"
                                viewBox="0 0 38 32"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M0.444 9.543L0.459 30.995L6.487 30.991L6.472 9.539L0.444 9.543V9.543Z" />
                                <path d="M8.05 0.961001L8.071 30.989L14.099 30.985L14.078 0.957001L8.05 0.961001V0.961001Z" />
                                <path d="M15.684 22.271L15.69 30.984L21.718 30.98L21.712 22.267L15.684 22.271V22.271Z" />
                                <path d="M23.291 14.201L23.303 30.979L29.331 30.975L29.319 14.197L23.291 14.201V14.201Z" />
                                <path d="M30.902 3.009L30.921 30.974L36.949 30.97L36.93 3.005L30.902 3.009V3.009Z" />
                            </svg>
                        </button>
                    </div>
                ) : null}
            </div>
            <div className="chart-area">
                <div className="subheader-graph">
                    {legendsList && (
                        <ul className="legends-list">
                            {legendsList.map(item => (
                                <li>
                                    <span
                                        className="brick"
                                        style={{ background: `${item.color}` }}
                                    />
                                    <span className="legend-text">
                                        {item.label}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {canSwitch && (
                        <div className="type-select">
                            <FormElems.Select
                                name="legends"
                                data={getLegendsOptions(legends)}
                                defaultSelected={selectedLabel}
                                labelField="name"
                                valueField="value"
                                handleChange={handleLegends}
                            />
                        </div>
                    )}
                </div>
                {isEmpty(granularity) ||
                checkTabData(timeSeriesValues, selectedLabel) ? (
                    <ChartNoData title="No data available" />
                ) : (
                    <Charts
                        type={chartType}
                        data={getFormattedGranularity(
                            timeSeriesValues,
                            granularity,
                            selectedLabel
                        )}
                        legends={legends}
                        staticPayValues={staticPayValues}
                    />
                )}
            </div>
        </div>
    );
};

GraphSwitch.propTypes = {
    customClass: PropTypes.string,
    defaultType: PropTypes.string,
    title: PropTypes.string,
    canSwitch: PropTypes.bool,
    data: PropTypes.array,
    legends: PropTypes.object,
};

export default GraphSwitch;
